import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import client from './api/client';
import PageHeader from './components/PageHeader';
import './UserProfile.css';

function UserProfile() {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);

  const sortedCorpHistory = useMemo(() => {
    const history = profileData?.corporation_history;
    if (!history) return [];
    return [...history].sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
  }, [profileData?.corporation_history]);
  navigateRef.current = navigate;

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await client.get('/auth/profile/');
        setProfileData(response.data);
      } catch (error) {
        console.error('Error fetching profile:', error);
        if (error.response?.status === 401) {
          navigateRef.current('/');
        } else {
          setError('Failed to load profile data');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';

    const date = new Date(dateString);
    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };

    // Format with ordinal suffix for day
    const day = date.getDate();
    const suffix = ['th', 'st', 'nd', 'rd'][((day % 100) - 20) % 10] || ['th', 'st', 'nd', 'rd'][day % 10] || 'th';

    return date.toLocaleDateString('en-US', options).replace(/\d+/, day + suffix);
  };

  const formatSecurityStatus = (status) => {
    if (status === undefined || status === null) return 'Unknown';
    return status.toFixed(2);
  };

  const getSecurityStatusColor = (status) => {
    if (status === undefined || status === null) return 'var(--text-faint)';
    if (status >= 5.0) return 'var(--pos)';
    if (status >= 0.0) return 'var(--warn)';
    return 'var(--neg)';
  };

  if (loading) {
    return (
      <div className="page">
        <PageHeader title="Character Profile" />
        <div className="page-status">Loading profile...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <PageHeader title="Character Profile" />
        <div className="page-status error">{error}</div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="page">
        <PageHeader title="Character Profile" />
        <div className="page-status">No profile data available.</div>
      </div>
    );
  }

  const { character } = profileData;

  return (
    <div className="page">
      <PageHeader title={character.name} meta={[character.corporation_name, character.alliance_name]} />

      <div className="page-content">
        <section className="panel profile-summary">
          <img
            className="profile-portrait"
            src={character.portrait_256x256 || character.portrait_512x512}
            alt=""
            onError={(e) => {
              // Fall back through the smaller portrait sizes
              if (e.target.src === character.portrait_256x256) {
                e.target.src = character.portrait_128x128;
              } else {
                e.target.src = character.portrait_64x64;
              }
            }}
          />
          <div className="stat-grid">
            <div className="stat">
              <span>Security status</span>
              <b style={{ color: getSecurityStatusColor(character.security_status) }}>
                {formatSecurityStatus(character.security_status)}
              </b>
            </div>
            <div className="stat"><span>Birthday</span><b>{formatDate(character.birthday)}</b></div>
            <div className="stat"><span>Corporation</span><b>{character.corporation_name || 'Unknown'}</b></div>
            <div className="stat"><span>Alliance</span><b>{character.alliance_name || 'None'}</b></div>
          </div>
        </section>

        {sortedCorpHistory.length > 0 && (
          <section className="panel">
            <div className="panel-head"><h2>Corporation history</h2></div>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Corporation</th>
                  <th className="num">Joined</th>
                </tr>
              </thead>
              <tbody>
                {sortedCorpHistory.map((record, index) => (
                  <tr key={record.record_id || index}>
                    <td>
                      {record.is_deleted
                        ? <><span className="text-faint">Deleted corporation</span> <span className="pill pill-neg">Deleted</span></>
                        : record.corporation_name || 'Unknown corporation'}
                    </td>
                    <td className="num">
                      {new Date(record.start_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
      </div>
    </div>
  );
}

export default UserProfile;